import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type React from 'react';
import { useRef, useState } from 'react';
import useProcessImage from '../../../hooks/use-process-image';
import { IdDocImageUploadError } from '../../../types';
import handleFileUpload from '../../../utils/handle-file-upload';
type DraggableInputFieldProps = {
  children?: React.ReactNode;
  hasError?: boolean;
  height: number;
  onComplete: (image: File) => void;
  isLoading?: boolean;
  onUploadError: (errors: IdDocImageUploadError[]) => void;
  allowPdf?: boolean;
};
const DraggableInputField = t0 => {
  const $ = _c(36);
  const {
    children,
    hasError,
    height,
    onComplete,
    isLoading,
    onUploadError,
    allowPdf
  } = t0;
  const [dragActive, setDragActive] = useState(false);
  const uploadPhotoRef = useRef(undefined);
  let t1;
  if ($[0] !== allowPdf) {
    t1 = {
      allowPdf
    };
    $[0] = allowPdf;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    acceptedFileFormats
  } = useProcessImage(t1);
  let t2;
  if ($[2] !== acceptedFileFormats || $[3] !== onComplete || $[4] !== onUploadError) {
    t2 = ev => {
      ev.preventDefault();
      setDragActive(false);
      if (ev.dataTransfer.items) {
        const files = [];
        [...ev.dataTransfer.items].forEach(item => {
          if (item.kind === "file") {
            const file = item.getAsFile();
            if (file) {
              files.push(file);
            }
          }
        });
        const result = handleFileUpload({
          acceptedFileFormats,
          files
        });
        if (!result.ok) {
          onUploadError(result.errors);
          return;
        }
        onComplete(result.file);
      } else {
        onUploadError([IdDocImageUploadError.unknownUploadError]);
      }
    };
    $[2] = acceptedFileFormats;
    $[3] = onComplete;
    $[4] = onUploadError;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleFileDrop = t2;
  const handleDrag = _temp;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ev_1 => {
      ev_1.preventDefault();
      setDragActive(true);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleDragEnter = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ev_2 => {
      ev_2.preventDefault();
      setDragActive(false);
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleDragLeave = t4;
  let t5;
  if ($[8] !== acceptedFileFormats || $[9] !== onComplete || $[10] !== onUploadError) {
    t5 = ev_3 => {
      const {
        files: files_0
      } = ev_3.target;
      const result_0 = handleFileUpload({
        acceptedFileFormats,
        files: files_0
      });
      if (!result_0.ok) {
        onUploadError(result_0.errors);
        return;
      }
      onComplete(result_0.file);
    };
    $[8] = acceptedFileFormats;
    $[9] = onComplete;
    $[10] = onUploadError;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleImageUpload = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      uploadPhotoRef.current?.click();
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleUpload = t6;
  const t7 = !!isLoading;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleUpload();
      }
    };
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = hasError && "bg-secondary border-solid border-error";
  const t10 = isLoading && "bg-neutral pointer-events-none *:pointer-events-none";
  const t11 = dragActive && "bg-active *:pointer-events-none";
  const t12 = !hasError && (dragActive ? "border-secondary" : "border-primary");
  let t13;
  if ($[14] !== t10 || $[15] !== t11 || $[16] !== t12 || $[17] !== t9) {
    t13 = cx("flex flex-col justify-center items-center border border-dashed rounded cursor-pointer", t9, t10, t11, t12, "active:border-secondary");
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t9;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = `${height}px`;
  let t15;
  if ($[19] !== t14) {
    t15 = {
      minHeight: t14
    };
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== children || $[22] !== handleFileDrop || $[23] !== hasError || $[24] !== t13 || $[25] !== t15 || $[26] !== t7) {
    t16 = <div role="button" tabIndex={0} onDrop={handleFileDrop} onDragOver={handleDrag} onDragLeave={handleDragLeave} onDragEnter={handleDragEnter} data-error={hasError} data-loading={t7} onClick={handleUpload} onKeyDown={t8} className={t13} style={t15}>{children}</div>;
    $[21] = children;
    $[22] = handleFileDrop;
    $[23] = hasError;
    $[24] = t13;
    $[25] = t15;
    $[26] = t7;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== acceptedFileFormats) {
    t17 = acceptedFileFormats.join(",");
    $[28] = acceptedFileFormats;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== handleImageUpload || $[31] !== t17) {
    t18 = <input ref={uploadPhotoRef as React.RefObject<HTMLInputElement | null>} type="file" accept={t17} onChange={handleImageUpload} aria-label="file-input" className="hidden" />;
    $[30] = handleImageUpload;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t16 || $[34] !== t18) {
    t19 = <div className="gap-6">{t16}{t18}</div>;
    $[33] = t16;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  return t19;
};
export default DraggableInputField;
function _temp(ev_0) {
  ev_0.preventDefault();
}